'use client';

import Link from 'next/link';
import { PublicPageLayout } from "@letsgo/PublicPageLayout";
import { Box, Typography, Button, Link as MuiLink } from '@mui/material';
import { SUPPORT_EMAIL } from '@/lib/site-contact';

const SupportPage = () => {
  return (
    <PublicPageLayout>
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={700} mb={3} color="text.primary">
          How Can We Help You?
        </Typography>
        <Typography variant="h6" color="text.secondary" mb={5}>
          Our team is here to provide the support you need.
        </Typography>
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 2 }}>
          <Typography variant="body1" color="text.primary" mb={4}>
            Whether you have questions about your account, features, billing, or need technical assistance, we&apos;re ready to assist. Below are some ways to get in touch or find answers to common questions.
          </Typography>

          {/* Contact Options */}
          <Typography variant="h5" fontWeight={700} mb={3} textAlign="left" color="text.primary">
            Contact Our Team
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 4 }}>
            <Button
              component={Link}
              href="/contact?category=general"
              variant="contained"
              color="primary"
              size="large"
              sx={(theme) => ({
                flexGrow: 1,
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark || theme.palette.primary.dark} 90%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              })}
            >
              General Inquiry
            </Button>
            <Button
              component={Link}
              href="/contact?category=technical"
              variant="outlined"
              color="secondary"
              size="large"
              sx={{
                flexGrow: 1,
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderColor: 'text.secondary',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              Technical Support
            </Button>
            <Button
              component={Link}
              href="/contact?category=billing"
              variant="outlined"
              color="secondary"
              size="large"
              sx={{
                flexGrow: 1,
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderColor: 'text.secondary',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              Billing & Plans
            </Button>
          </Box>

          {/* SLA / Response Times */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 4 }}>
            Typical response time for inquiries is **24-48 business hours**. For critical issues, please specify urgency in your message.
          </Typography>

          {/* Additional Resources */}
          <Typography variant="h5" fontWeight={700} mb={3} textAlign="left" color="text.primary">
            Additional Resources
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Button
              component={Link}
              href="/faq"
              variant="text"
              size="large"
              sx={{
                flexGrow: 1,
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 'bold',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              Visit our FAQ Page
            </Button>
            <Button
              component={Link}
              href="/getting-started"
              variant="text"
              size="large"
              sx={{
                flexGrow: 1,
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 'bold',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              Getting Started Guide
            </Button>
            <Button
              component={Link}
              href="/tutorials"
              variant="text"
              size="large"
              sx={{
                flexGrow: 1,
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 'bold',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              Guides & Tutorials
            </Button>
            <Button
              component={Link}
              href="/community"
              aria-label="Community — coming soon; visit the page to get notified or contact us"
              variant="text"
              size="large"
              sx={{
                flexGrow: 1,
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 'bold',
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'action.hover',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
            >
              Community
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Alternatively, you can email us directly at{' '}
            <MuiLink href={`mailto:${SUPPORT_EMAIL}`} color="primary">
              {SUPPORT_EMAIL}
            </MuiLink>{' '}
            for assistance.
          </Typography>
        </Box>
      </Box>
    </PublicPageLayout>
  );
};

export default SupportPage;
